import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple, X, SignOut } from "@phosphor-icons/react";
import { api, CATEGORIES } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const EMPTY = {
  full_name: "",
  category: "Politicians",
  description: "",
  region: "",
  severity: 3,
  sources: "",
  photo: "",
};

export default function Admin() {
  const { t, tc } = useLang();
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/people");
    setPeople(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      full_name: p.full_name,
      category: p.category,
      description: p.description || "",
      region: p.region || "",
      severity: p.severity || 3,
      sources: (p.sources || []).join("\n"),
      photo: p.photo || "",
    });
    setOpen(true);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("admin.toastImgLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error(t("admin.toastNameRequired"));
      return;
    }
    setSaving(true);
    const payload = {
      full_name: form.full_name.trim(),
      category: form.category,
      description: form.description,
      region: form.region || null,
      severity: Number(form.severity),
      sources: form.sources
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      photo: form.photo || null,
    };
    try {
      if (editing) {
        await api.put(`/people/${editing.id}`, payload);
        toast.success(t("admin.toastUpdated"));
      } else {
        await api.post("/people", payload);
        toast.success(t("admin.toastAdded"));
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(t("admin.toastSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("admin.confirmDelete").replace("{name}", p.full_name))) return;
    try {
      await api.delete(`/people/${p.id}`);
      toast.success(t("admin.toastDeleted"));
      await load();
    } catch (e) {
      toast.error(t("admin.toastDeleteFailed"));
    }
  };

  return (
    <div className="px-6 md:px-12 py-28" data-testid="admin-page">
      <div className="flex flex-col gap-6 border-b border-border pb-8 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
            {t("admin.controlRoom")}
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">
            {t("admin.dashboard")}
          </h1>
        </div>
        <div className="flex items-center gap-3">
        <Button
          onClick={openNew}
          data-testid="add-record-btn"
          className="rounded-none bg-foreground text-background hover:bg-primary font-mono text-xs uppercase tracking-[0.2em] h-12 px-6"
        >
          <Plus size={16} weight="bold" className="mr-2" /> {t("admin.addRecord")}
        </Button>
        <button
          onClick={async () => {
            try {
              await api.post("/auth/logout");
            } catch (e) {
              /* ignore */
            }
            window.location.href = "/";
          }}
          data-testid="logout-btn"
          className="border border-foreground px-5 h-12 font-mono text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors inline-flex items-center gap-2"
        >
          <SignOut size={14} weight="bold" /> {t("admin.logout")}
        </button>
        </div>
      </div>

      <div className="mt-8 border border-border" data-testid="admin-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("admin.thPhoto")}</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("admin.thName")}</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("admin.thCategory")}</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("admin.thRegion")}</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("admin.thSev")}</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-right">{t("admin.thActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.map((p) => (
              <TableRow key={p.id} className="border-border" data-testid={`admin-row-${p.id}`}>
                <TableCell>
                  <div className="h-12 w-12 overflow-hidden bg-secondary">
                    {p.photo ? (
                      <img src={p.photo} alt="" className="h-full w-full object-cover grayscale" />
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="font-display font-bold uppercase">{p.full_name}</TableCell>
                <TableCell className="font-mono text-xs uppercase">{tc(p.category)}</TableCell>
                <TableCell className="font-mono text-xs">{p.region || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.severity}/5</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`edit-btn-${p.id}`}
                      className="border border-border p-2 hover:bg-foreground hover:text-background transition-colors"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      data-testid={`delete-btn-${p.id}`}
                      className="border border-border p-2 hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {people.length === 0 && (
          <div className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            {t("admin.noRecords")}
          </div>
        )}
      </div>

      {/* FORM DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tighter text-2xl">
              {editing ? t("admin.editRecord") : t("admin.newRecord")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 font-mono">
            {/* Photo */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.photo")}</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="h-20 w-20 overflow-hidden bg-secondary border border-border relative">
                  {form.photo ? (
                    <>
                      <img src={form.photo} alt="" className="h-full w-full object-cover grayscale" />
                      <button
                        onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                        className="absolute right-0 top-0 bg-foreground text-background p-0.5"
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </>
                  ) : null}
                </div>
                <label
                  className="cursor-pointer border border-foreground px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors inline-flex items-center gap-2"
                  data-testid="upload-label"
                >
                  <UploadSimple size={14} weight="bold" /> {t("admin.upload")}
                  <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="photo-input" />
                </label>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.fullName")}</label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-2 rounded-none border-border font-mono"
                data-testid="input-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.category")}</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-2 rounded-none border-border font-mono text-xs" data-testid="input-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none font-mono">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{tc(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.severity")}</label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  className="mt-2 rounded-none border-border font-mono"
                  data-testid="input-severity"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.region")}</label>
              <Input
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                className="mt-2 rounded-none border-border font-mono"
                data-testid="input-region"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.description")}</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="mt-2 rounded-none border-border font-mono"
                data-testid="input-description"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("admin.sources")}</label>
              <Textarea
                value={form.sources}
                onChange={(e) => setForm({ ...form, sources: e.target.value })}
                rows={3}
                className="mt-2 rounded-none border-border font-mono"
                data-testid="input-sources"
              />
            </div>

            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-record-btn"
              className="w-full rounded-none bg-foreground text-background hover:bg-primary font-mono text-xs uppercase tracking-[0.2em] h-12"
            >
              {saving ? t("admin.saving") : editing ? t("admin.updateRecord") : t("admin.createRecord")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
